import { Component, OnInit, Input, OnChanges, Output, EventEmitter, ElementRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { TooltipPosition } from '@angular/material/tooltip';
import { MatCheckboxChange } from '@angular/material/checkbox';

declare let $: any; // jQuery

import { Form } from '../../../../models/form-builder/form.model';
import { DataCollectionService } from '../../../../services/data-collection.service';
import { Workflow } from '../../../../models/site-content/workflow.model';
import { IDrawerComponentLiaison } from '../../../../interfaces/idrawer-component-liaison';
import { ScriptableBaseComponent } from '../../../../components/scriptable-base/scriptable-base.component';
import { Scriptable } from '../../../../models/component-scripting/scriptable-function.annotation';
import { AdditionalElementInfo, IAdditionalElementInfo, INameToPrettyNameMap } from '../../../../models/component-scripting/additional-element-info.model';

@Component({
    selector: 'app-form-properties',
    templateUrl: './form-properties.component.html',
    styleUrls: ['./form-properties.component.scss', '../../../properties.scss'],
    standalone: false
})
export class FormPropertiesComponent extends ScriptableBaseComponent implements OnInit, OnChanges {
    @Input() form: Form;
    @Input() drawerComponentLiaison: IDrawerComponentLiaison = null;

    @Output()
    onClose: EventEmitter<boolean> = new EventEmitter();

    originalForm: Form;

    private iMaxCellCount: number = 0;
    private iMaxCellColSpan: number = 0;

    private static readonly layouts: any[] =
        [
            {
                columns: 1,
                alignment: 'justify',
                values: [12]
            },
            {
                columns: 2,
                alignment: 'justify',
                values: [6, 6]
            },
            {
                columns: 2,
                alignment: 'left',
                values: [8, 4]
            },
            {
                columns: 2,
                alignment: 'right',
                values: [4, 8]
            },
            {
                columns: 3,
                alignment: 'justify',
                values: [4, 4, 4]
            },
            {
                columns: 3,
                alignment: 'left',
                values: [6, 3, 3]
            },
            {
                columns: 3,
                alignment: 'center',
                values: [3, 6, 3]
            },
            {
                columns: 3,
                alignment: 'right',
                values: [3, 3, 6]
            },
            {
                columns: 4,
                alignment: 'justify',
                values: [3, 3, 3, 3]
            },
            {
                columns: 4,
                alignment: 'left',
                values: [4, 4, 2, 2]
            },
            {
                columns: 4,
                alignment: 'center',
                values: [2, 4, 4, 2]
            },
            {
                columns: 4,
                alignment: 'right',
                values: [2, 2, 4, 4]
            }
        ];

    private siteWorkflows: Workflow[] = [];

    private formGroup: UntypedFormGroup = null;
    private alwaysShowCellSelectionFormControl: UntypedFormControl = null;
    private alwaysShowCellSelectionIsModified: boolean = false;

    // Constructor.
    public constructor(private dataCollectionService: DataCollectionService, private _formBuilder: UntypedFormBuilder, elForBaseClass: ElementRef)
    {
        super(elForBaseClass);
        let propertiesButtonNameMap: INameToPrettyNameMap =
        {
            '1': 'Columns per Row: 1',
            '2': 'Columns per Row: 2',
            '3': 'Columns per Row: 3',
            '4': 'Columns per Row: 4',
            'format_align_justify': 'Align: Justify',
            'format_align_left': 'Align: Left',
            'format_align_center': 'Align: Center',
            'format_align_right': 'Align: Right',
        };
        this.element('#formNameDiv').hasInput().withNames(['Form Name']);
        this.element('#formDefaultColumnCountDiv').hasButtons().withNameToPrettyNameMap(propertiesButtonNameMap).hasElementSubType('# Columns');//.hasMatCheckBox();
        this.element('#formAlignmentDiv').hasButtons().withNameToPrettyNameMap(propertiesButtonNameMap).hasElementSubType('Alignment');
        let actionsButtonNameMap: INameToPrettyNameMap =
        {
            'OK': 'Save Properties',
            'Cancel': 'Cancel Without Saving'
        };
        this.element('#dialogActionsDiv').hasButtons().withNameToPrettyNameMap(actionsButtonNameMap).hasElementSubType('Save/Cancel');
    }

    public ngOnInit() {
        super.ngOnInit(); // Important to call this as the first line in this method.

        // Manually initialize my drawer liaison (since we are not inheriting from DrawerComponentBase).
        if (this.drawerComponentLiaison != null)
            this.drawerComponentLiaison.drawerComponentInitialized(this);

        // Calculate the max row cell count.
        this.iMaxCellCount = this.form.formLayout.MaxRowCellCountWithContent;

        // Calculate the max col span
        // for a cell with content.
        this.iMaxCellColSpan = this.form.formLayout.MaxCellColSpanWithContent;

        // Get the latest site workflows.
        this.retrieveSiteWorkflows();

        this.setupFormGroup();
        this.setupAutonumberFormNamePrefixFormControl(); // Added 07-11-2024.
    }

    public ngOnChanges() {
        // 10-07-2022:  still testing replacing the following line with the line commented out immediately below.
        //
        //              The idea is to replace calls to Object.assign(), which does not copy method references,
        //              with calls to a class's clone() method.
        this.originalForm = Object.assign({}, this.form);
        //this.originalForm = <Form>this.form.clone();
    }

    // Accessor methods called by the HTML code.
    public get MaxCellCount(): number {
        return (this.iMaxCellCount);
    }

    public get MaxCellColSpan(): number {
        return (this.iMaxCellColSpan);
    }

    public get TooltipPosition(): TooltipPosition {
        let position: TooltipPosition = 'below';

        return (position);
    }

    public ColumnCountTooltip(iColCountOption: number): string {
        let bColCountDisabled: boolean = ((iColCountOption < this.iMaxCellCount) && (iColCountOption < 4));

        let tooltip: string;
        if (bColCountDisabled) {
            tooltip =
                `Column count ${iColCountOption} option is disabled because at ` +
                `least one form field spans at least ${iColCountOption + 1} cells`;
        } else {
            tooltip =
                `Click here to set ${iColCountOption} as the default form column count`;
        }

        return (tooltip);
    }

    public get SiteWorkflows(): Workflow[] {
        return this.siteWorkflows;
    }

    public get FormGroup(): UntypedFormGroup {
        return this.formGroup;
    }
    public get AlwaysShowCellSelectionFormControl(): UntypedFormControl {
        return this.alwaysShowCellSelectionFormControl;
    }

    public get SuggestedFormNamePrefix_FormControl(): UntypedFormControl {
        return this.form.transient_suggestedFormNamePrefix_formControl;
    }
    public get ExampleSuggestedFormName(): string {
        /*
        let exampleFormName: string = '';

        if ((this.form.suggestAutonumberFormNames) && (this.form.suggestedFormNamePrefix != null) && (this.form.suggestedFormNamePrefix.trim() != '')) {
            if (this.form.prefixFormNameWithFolderName)
                exampleFormName = '[FolderName] ';

            exampleFormName += this.form.suggestedFormNamePrefix;
            exampleFormName += '5';
        }

        return exampleFormName;
        */
        let exampleFormName: string = this.form.proposeAutoNumberName(4, '[FolderName]');
        return exampleFormName;
    }

    public get AlwaysShowCellSelectionIsModified(): boolean {
        return this.alwaysShowCellSelectionIsModified;
    }

    // Event handler methods.
    //@Scriptable('Always Show Cell Selection Updated')
    public alwaysShowCellSelectionUpdated(): void {
    }
    @Scriptable('Toggle Always Show Cell Selection')
    public toggleAlwaysShowCellSelection(): void {
        this.form.alwaysShowCellSelection = !this.form.alwaysShowCellSelection;
    }

    /**
     * Fires when the Ok button for the form properties component drawer is clicked */
    public saveClicked(): void {

        //**Apply selected columns and column layout
        //let layout = this.layouts.filter(x => x.columns == this.form.formLayout.columnsDefault && x.alignment == this.form.formLayout.alignment)[0];
        let layout = FormPropertiesComponent.layouts.filter(x => x.columns == this.form.formLayout.columnsDefault && x.alignment == this.form.formLayout.alignment)[0];

        if (layout) {
            this.form.formLayout.columnWidths = layout.values;
        }

        this.onClose.emit(true);
    }

    public cancelClicked(): void {
        this.form.assignFrom(this.originalForm);

        this.onClose.emit(false);
    }

    public suggestAutonumberFormNamesChanged(eventData: MatCheckboxChange): void {
        this.form.suggestAutonumberFormNames = eventData.checked;
    }
    public prefixFormNameWithFolderNameChanged(eventData: MatCheckboxChange): void {
        this.form.prefixFormNameWithFolderName = eventData.checked;
    }

    // Helper methods.
    private retrieveSiteWorkflows(): void {
        this.dataCollectionService.getWorkflowsBySite(this.form.dataCollectionId).then(workflows => {
            this.siteWorkflows = workflows;
        });
    }

    private setupFormGroup(): void {
        this.formGroup = this._formBuilder.group({
            alwaysShowCellSelection: this.form.alwaysShowCellSelection
        });
        //this.alwaysShowCellSelectionFormControl = new FormControl({ alwaysShowCellSelection: this.form.alwaysShowCellSelection });
        this.alwaysShowCellSelectionFormControl = new UntypedFormControl(this.form.alwaysShowCellSelection);
    }

    /**
     * Updates the flag which tells if the
     * 'Always Show Selection' checkbox has been modified*/
    public ToggleAlwaysShowCellSelectionIsModified(): void {
        this.alwaysShowCellSelectionIsModified = !this.alwaysShowCellSelectionIsModified;
    }

    private setupAutonumberFormNamePrefixFormControl(): void {
        this.form.transient_suggestedFormNamePrefix_formControl = new UntypedFormControl(this.form.suggestedFormNamePrefix);
        this.form.transient_suggestedFormNamePrefix_formControl.valueChanges.subscribe(value => {
            this.form.suggestedFormNamePrefix = value;
        });
    }
}
